import { React, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Loading from "../views/Loading";
import PatientNavbar from "../components/PatientNavbar";
import PatientSidebar from "../components/PatientSidebar";
import { useTranslation } from "../contexts/TranslationContext";

const getFirstName = (name) => {
  if (name.lastIndexOf(" ") === -1) {
    return name;
  }
  return name.substring(0, name.lastIndexOf(" ")).trim();
};

const getLastName = (name) => {
  if (name.lastIndexOf(" ") === -1) {
    return "";
  }
  return name.substring(name.lastIndexOf(" ")).trim();
  };
  
  export default function AdminPayment() {
    const { id } = useParams();
    const stripe = useStripe();
    const elements = useElements();
    
    const { translate } = useTranslation();
  const [clientSecret, setClientSecret] = useState("");
  const [card, setCard] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [ready, setReady] = useState(false);

  const getPaymentMethods = async (patientID) => {
    await fetch(
      `${process.env.REACT_APP_API}/patient/${patientID}/clinic_get_patient_payment_methods`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.success) {
          if (Object.keys(response.payment_method).length > 0) {
            const payment_method = response.payment_method;
            setCard({
              cardNumber: `···· ···· ···· ${payment_method.card.last4}`,
              expires: `${payment_method.card.exp_month}/${payment_method.card.exp_year}`,
              cvv: "···",
              cardHolderFirstName: getFirstName(
                payment_method.billing_details.name
              ),
              cardHolderLastName: getLastName(
                payment_method.billing_details.name
              ),
              billingAddress: {
                street: payment_method.billing_details.address.line1,
                city: payment_method.billing_details.address.city,
                state: payment_method.billing_details.address.state,
                zip: payment_method.billing_details.address.postal_code,
              },
              phone: payment_method.billing_details.phone,
              email: payment_method.billing_details.email,
            });
            setEditCard({
              cardHolderFirstName: getFirstName(
                payment_method.billing_details.name
              ),
              cardHolderLastName: getLastName(
                payment_method.billing_details.name
              ),
              billingAddress: {
                street: payment_method.billing_details.address.line1,
                city: payment_method.billing_details.address.city,
                state: payment_method.billing_details.address.state,
                zip: payment_method.billing_details.address.postal_code,
              },
              phone: payment_method.billing_details.phone,
              email: payment_method.billing_details.email,
            });
          } else {
            setCard({
              cardNumber: "",
              expires: "",
              cvv: "",
              cardHolderFirstName: "",
              cardHolderLastName: "",
              billingAddress: {
                street: "",
                city: "",
                state: "",
                zip: "",
              },
              phone: "",
              email: "",
            });
            setEditCard({
              cardHolderFirstName: "",
              cardHolderLastName: "",
              billingAddress: {
                street: "",
                city: "",
                state: "",
                zip: "",
              },
              phone: "",
              email: "",
            });
          }
        } else {
          setMessage(response.message);
        }
      });
  };

  const initAddPaymentMethod = async (patientID) => {
    await fetch(
      `${process.env.REACT_APP_API}/patient/${patientID}/clinic_init_add_patient_payment_method`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      }
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.success) {
          setClientSecret(response.intent.client_secret);
        } else {
          setMessage(response.message);
        }
      });
  };

  const isCardValid = () => {
    return true;
  };

  const saveCard = (patientID) => {
    setMessage("");
    setSuccess("");
    if (isCardValid() && stripe && elements) {
      setReady(false);
      const cardElement = elements.getElement(CardElement);
      stripe
        .confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              address: {
                city: editCard.billingAddress.city,
                line1: editCard.billingAddress.street,
                postal_code: editCard.billingAddress.zip,
                state: editCard.billingAddress.state,
              },
              email: editCard.email,
              name:
                editCard.cardHolderFirstName +
                " " +
                editCard.cardHolderLastName,
              phone: editCard.phone,
            },
          },
        })
        .then(function (result) {
          if (result.error) {
            setMessage(result.error.message);
            setReady(true);
          } else {
            fetch(
              `${process.env.REACT_APP_API}/patient/${patientID}/clinic_add_patient_payment_method`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem(
                    "access_token"
                  )}`,
                },
                body: JSON.stringify({
                  payment_method: result.setupIntent.payment_method,
                }),
              }
            )
              .then((response) => response.json())
              .then((response) => {
                if (response.success) {
                  Promise.all([
                    getPaymentMethods(),
                    initAddPaymentMethod(),
                  ]).then(() => {
                    setSuccess(response.message);
                    setEditMode(false);
                    setReady(true);
                  });
                } else {
                  setMessage(response.message);
                  setReady(true);
                }
              })
              .catch((error) => {
                console.error(error);
                setMessage(translate('error_occurred'));
                setReady(true);
              });
          }
        })
        .catch((error) => {
          console.error(error);
          setMessage(translate('error_occurred'));
          setReady(true);
        });
    }
  };

  useEffect(() => {
    Promise.all([getPaymentMethods(id), initAddPaymentMethod(id)])
      .then(() => {
        setReady(true);
      })
      .catch((error) => {
        console.error(error);
        setMessage(translate('error_occurred'));
        setReady(true);
      });
  }, [id]);

  return (
    <>
      <main
        className={`mx-auto container flex flex-col p-4 font-font-2 tracking-wider ${
          ready ? "" : "hidden"
        }`}
      >
        <PatientNavbar id={id} tab={1} />
        {message && <p className="mt-8 text-center text-red-600">{message}</p>}
        {success && (
          <p className="mt-8 text-center text-green-600">{success}</p>
        )}
        <section className="flex">
          <PatientSidebar id={id} />
          {card && (
            <div className="mx-auto">
              <form
                className="mt-4 flex flex-col"
                onSubmit={(event) => {
                  event.preventDefault();
                  saveCard(id);
                }}
              >
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    className="px-8 py-1 bg-background-23 rounded-lg text-text-3 font-bold
              cursor-pointer focus:outline-none"
                    onClick={() => {
                      setEditMode(!editMode);
                    }}
                  >
                    {editMode ? "Cancel" : "Edit"}
                  </button>
                  <p className="mx-4 text-text-2">{translate('payment_information')}</p>
                  <button
                    className="px-8 py-1 bg-background-23 rounded-lg text-text-3 font-bold
              cursor-pointer focus:outline-none"
                    disabled={!editMode}
                  >
                    {translate('save')}
                  </button>
                </div>
                <div className="mx-auto mt-10 flex flex-col items-end">
                  <div
                    className={`w-full md:w-96 px-2 ${
                      editMode ? "" : "hidden"
                    }`}
                  >
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "20px",
                            color: "#bfc5ca",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                  {!editMode && (
                    <>
                      <label className="mt-4 flex items-center">
                        <p className="ml-2">{translate('card_number')}</p>
                        <input
                          type="text"
                          className="ml-6 w-40 md:w-96 px-2 py-1 bg-background-2 font-bold text-text-2 rounded-sm focus:outline-none"
                          value={
                            editMode ? editCard.cardNumber : card.cardNumber
                          }
                          disabled={!editMode}
                          onChange={(event) => {
                            setEditCard({
                              ...editCard,
                              cardNumber: event.target.value,
                            });
                          }}
                        />
                      </label>
                      <div className="mt-4 flex flex-col items-end md:flex-row">
                        <label className="flex items-center">
                          <p className="ml-2">{translate('expires')}</p>
                          <input
                            type="text"
                            className="ml-6 md:w-46 font-bold w-40 px-2 py-1 bg-background-2 text-text-2 rounded-sm focus:outline-none"
                            value={editMode ? editCard.expires : card.expires}
                            disabled
                          />
                        </label>
                        <label className="mt-4 md:mt-0 md:ml-5 flex items-center">
                          <p className="font-normal">{translate('cvv')}</p>
                          <input
                            type="text"
                            className="ml-5 w-40 md:w-32 font-bold px-2 py-1 bg-background-2 text-text-2 rounded-sm focus:outline-none"
                            value={editMode ? editCard.cvv : card.cvv}
                            disabled
                          />
                        </label>
                      </div>
                    </>
                  )}
                  <div className="mt-4 flex">
                    <p className="ml-2 mt-1">{translate('card_holder_name')}</p>
                    <div className="ml-6 grid gap-4 w-40 md:w-96 md:grid-cols-2">
                      <label className="flex items-center">
                        <input
                          type="text"
                          className="w-full px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                          value={
                            editMode
                              ? editCard.cardHolderFirstName
                              : card.cardHolderFirstName
                          }
                          disabled={!editMode}
                          placeholder={translate('first_name')}
                          required
                          onChange={(event) => {
                            setEditCard({
                              ...editCard,
                              cardHolderFirstName: event.target.value,
                            });
                          }}
                        />
                      </label>
                      <label className="flex items-center">
                        <input
                          type="text"
                          className="w-full px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                          value={
                            editMode
                              ? editCard.cardHolderLastName
                              : card.cardHolderLastName
                          }
                          disabled={!editMode}
                          placeholder={translate('last_name')}
                          required
                          onChange={(event) => {
                            setEditCard({
                              ...editCard,
                              cardHolderLastName: event.target.value,
                            });
                          }}
                        />
                      </label>
                    </div>
                  </div>
                  <label className="mt-4 flex items-center">
                    <p className="ml-2">{translate('billing_address')}</p>
                    <input
                      type="text"
                      className="ml-6 w-40 md:w-96 px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                      value={
                        editMode
                          ? editCard.billingAddress.street
                          : card.billingAddress.street
                      }
                      disabled={!editMode}
                      placeholder={translate('street')}
                      required
                      onChange={(event) => {
                        setEditCard({
                          ...editCard,
                          billingAddress: {
                            ...editCard.billingAddress,
                            street: event.target.value,
                          },
                        });
                      }}
                    />
                  </label>
                  <input
                    type="text"
                    className="mt-4 ml-6 w-40 md:w-96 px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                    value={
                      editMode
                        ? editCard.billingAddress.city
                        : card.billingAddress.city
                    }
                    disabled={!editMode}
                    placeholder={translate('city')}
                    required
                    onChange={(event) => {
                      setEditCard({
                        ...editCard,
                        billingAddress: {
                          ...editCard.billingAddress,
                          city: event.target.value,
                        },
                      });
                    }}
                  />
                  <div className="mt-4 grid gap-4 w-40 md:w-96 md:grid-cols-2">
                    <label className="flex items-center">
                      <input
                        type="text"
                        className="w-full px-2 py-1 font-bold bg-background-2 text-text-2 rounded-sm focus:outline-none"
                        value={
                          editMode
                            ? editCard.billingAddress.state
                            : card.billingAddress.state
                        }
                        disabled={!editMode}
                        placeholder={translate('state')}
                        required
                        onChange={(event) => {
                          setEditCard({
                            ...editCard,
                            billingAddress: {
                              ...editCard.billingAddress,
                              state: event.target.value,
                            },
                          });
                        }}
                      />
                    </label>
                    <label className="flex items-center">
                      <input
                        type="text"
                        className="w-full px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                        value={
                          editMode
                            ? editCard.billingAddress.zip
                            : card.billingAddress.zip
                        }
                        disabled={!editMode}
                        placeholder={translate('state')}
                        required
                        onChange={(event) => {
                          setEditCard({
                            ...editCard,
                            billingAddress: {
                              ...editCard.billingAddress,
                              zip: event.target.value,
                            },
                          });
                        }}
                      />
                    </label>
                  </div>
                  <label className="mt-4 flex items-center">
                    <p className="ml-2">{translate('phone')}</p>
                    <input
                      type="text"
                      className="ml-6 w-40 md:w-96 px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                      value={editMode ? editCard.phone : card.phone}
                      disabled={!editMode}
                      required
                      onChange={(event) => {
                        setEditCard({
                          ...editCard,
                          phone: event.target.value,
                        });
                      }}
                    />
                  </label>
                  <label className="mt-4 flex items-center">
                    <p className="ml-2">{translate('email')}</p>
                    <input
                      type="email"
                      className="ml-6 w-40 md:w-96 px-2 py-1 bg-background-2 text-text-2 font-bold rounded-sm focus:outline-none"
                      value={editMode ? editCard.email : card.email}
                      disabled={!editMode}
                      required
                      onChange={(event) => {
                        setEditCard({
                          ...editCard,
                          email: event.target.value,
                        });
                      }}
                    />
                  </label>
                </div>
              </form>
            </div>
          )}
        </section>
      </main>
      <Loading message={translate('loading_payment_details')} ready={ready} />
    </>
  );
}
